import React, { useCallback, useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import DisplaySubsComponent from "../../subscription/Display";
import axiosInstance from "../../interceptor/axiosInstance";
import NotificationService from "../Utils/NotificationService";
import "./Org.css";

const defaultUrl =
  "https://chatgptopenai.blob.core.windows.net/public-files/default-avatar.png";
const defaultAgentUrl =
  "https://chatgptopenai.blob.core.windows.net/public-files/59/1750236384315_icons8-online-support-50.png";

const defaultColors = ["#007bff", "#28a745", "#ffc107", "#dc3545"];
const defaultColorsText = ["#000000", "#ffffff", "#D3D3D3", "#808080"];
const conversationFlowStorageKey = "chatbotConversationFlow";
const chatbotThemeStorageKey = "chatbotThemeSettings";

const flowInputTypes = [{ value: "text", label: "Text Input" }];

const createEmptyQuestion = () => ({
  id: null,
  question_label: "",
  input_type: "text",
  field_key: "",
  is_active: true,
  options_text: "",
});

const parseFlowOptions = (optionsText = "") =>
  optionsText
    .split(/[\n,]/)
    .map((option) => option.trim())
    .filter(Boolean);

const slugifyFieldKey = (value = "") =>
  value
    .toLowerCase()
    .trim()
    .replace(/^what\s+is\s+your\s+/i, "")
    .replace(/^what\s+is\s+/i, "")
    .replace(/^please\s+provide\s+your\s+/i, "")
    .replace(/^enter\s+your\s+/i, "")
    .replace(/^tell\s+me\s+your\s+/i, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const getInputTypeLabel = (type) =>
  flowInputTypes.find((item) => item.value === type)?.label || "Text Input";

const isTruthyValue = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  value === "true" ||
  value === "active";

const getPreviewResponse = (question) => {
  const options = parseFlowOptions(question.options_text);
  switch (question.input_type) {
    case "email":
      return "alex@example.com";
    case "phone":
      return "+1 (555) 123-4567";
    case "textarea":
      return "Thanks for reaching out. I would like to learn more about the service.";
    case "dropdown":
    case "multiple_choice":
      return `Selected: ${options[0] || "Sample option"}`;
    default:
      return "Sample answer";
  }
};


const normalizeConversationFlow = (rawFlow) => {
  if (!rawFlow) return [];
  let parsedFlow = rawFlow;
  if (typeof rawFlow === "string") {
    try {
      parsedFlow = JSON.parse(rawFlow);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(parsedFlow)) return [];
  return parsedFlow.map((item, index) => {
    const questionId = item.id ?? item.question_id ?? null;

    return {
      id: questionId ?? `question-${index + 1}`,
      question_id: questionId,
      question_label: item.question_label || item.label || item.question || "",
      input_type: String(item.input_type || item.type || "text").toLowerCase(),
      field_key: item.field_key || item.fieldKey || "",
      is_active:
        isTruthyValue(item.is_active) ||
        isTruthyValue(item.active) ||
        item.status === "active",
      options_text: Array.isArray(item.options)
        ? item.options.join("\n")
        : item.options_text || item.options || "",
    };
  });
};

const buildPreviewMessages = (welcomeMessage, conversationFlow, isQuestionActive = (question) => question.is_active) => {
  const previewMessages = [];
  if (welcomeMessage && welcomeMessage.trim()) {
    previewMessages.push({ type: "bot", text: welcomeMessage.trim() });
  }
  conversationFlow
    .filter((q) => isQuestionActive(q) && q.question_label.trim())
    .forEach((question) => {
      previewMessages.push({ type: "bot", text: question.question_label.trim(), meta: question });
      previewMessages.push({ type: "user", text: getPreviewResponse(question), meta: question });
    });
  return previewMessages;
};

// ── Sidebar nav items ──────────────────────────────────────────────────────────
const NAV_ITEMS = [
  {
    id: "flow",
    label: "Conversation Flow",
    icon: (
      <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path d="M14 1a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H4.414A2 2 0 0 0 3 11.586l-2 2V2a1 1 0 0 1 1-1h12zM2 0a2 2 0 0 0-2 2v12.793a.5.5 0 0 0 .854.353l2.853-2.853A1 1 0 0 1 4.414 12H14a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2z" />
      </svg>
    ),
  },
  {
    id: "appearance",
    label: "Appearance",
    icon: (
      <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path d="M8 5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm4 3a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM5.5 7a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm.5 6a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z" />
        <path d="M16 8c0 3.15-1.866 2.585-3.567 2.07C11.42 9.763 10.465 9.5 8 9.5c-2.464 0-3.42.263-4.433.57C1.866 10.585 0 11.15 0 8a8 8 0 1 1 16 0z" />
      </svg>
    ),
  },
  {
    id: "subscription",
    label: "Subscription",
    icon: (
      <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4zm2-1a1 1 0 0 0-1 1v1h14V4a1 1 0 0 0-1-1H2zm13 4H1v5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V7z" />
        <path d="M2 10a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-1z" />
      </svg>
    ),
  },
];

// ── ColorPicker helpers ────────────────────────────────────────────────────────
const ColorPicker = ({ label, value, onChange }) => (
  <div className="mb-3">
    <label className="form-label label-text fw-bold">{label}</label>
    <div className="d-flex gap-4 align-items-center flex-wrap align-content-start">
      {defaultColors.map((color) => (
        <div
          key={color}
          className="rounded-circle border"
          style={{ width: 40, height: 40, backgroundColor: color, cursor: "pointer" }}
          onClick={() => onChange(color)}
        />
      ))}
      <label className="position-relative" style={{ cursor: "pointer" }}>
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="opacity-0 position-absolute"
          style={{ width: 40, height: 40, cursor: "pointer" }}
        />
        <div
          className="rounded-circle border"
          style={{
            width: 40,
            height: 40,
            background: "conic-gradient(red, orange, yellow, green, blue, indigo, violet)",
            cursor: "pointer",
          }}
        />
      </label>
    </div>
  </div>
);

const ColorPickerText = ({ label, value, onChange }) => (
  <div className="mb-3">
    <label className="form-label label-text fw-bold">{label}</label>
    <div className="d-flex gap-4 align-items-center flex-wrap align-content-start">
      {defaultColorsText.map((color) => (
        <div
          key={color}
          className="rounded-circle border"
          style={{ width: 40, height: 40, backgroundColor: color, cursor: "pointer" }}
          onClick={() => onChange(color)}
        />
      ))}
      <label className="position-relative" style={{ cursor: "pointer" }}>
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="opacity-0 position-absolute"
          style={{ width: 40, height: 40, cursor: "pointer" }}
        />
        <div
          className="rounded-circle border"
          style={{
            width: 40,
            height: 40,
            background: "conic-gradient(red, orange, yellow, green, blue, indigo, violet)",
            cursor: "pointer",
          }}
        />
      </label>
    </div>
  </div>
);

// ── Confirmation Dialog ────────────────────────────────────────────────────────
const ConfirmDialog = ({ isOpen, title, message, onConfirm, onCancel }) => {
  if (!isOpen) return null;
  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 9999 }}
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-3 shadow-lg p-4"
        style={{ maxWidth: 420, width: "90%" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="d-flex align-items-center gap-2 mb-3">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
            style={{ width: 36, height: 36, backgroundColor: "#fee2e2" }}
          >
            <svg width="18" height="18" fill="#dc2626" viewBox="0 0 16 16">
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
              <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z" />
            </svg>
          </div>
          <h6 className="mb-0 fw-semibold text-dark">{title}</h6>
        </div>
        <p className="text-muted mb-4" style={{ fontSize: "0.9rem" }}>{message}</p>
        <div className="d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-sm btn-outline-secondary px-3" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn btn-sm btn-danger px-3" onClick={onConfirm}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
const SetupPage = () => {
  const [activeTab, setActiveTab] = useState("flow");

  // ── Theme state ──────────────────────────────────────────────────────────────
  const [chatbotTitle, setChatbotTitle] = useState("");
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const [headerBg, setHeaderBg] = useState("#007bff");
  const [headerTextColor, setHeaderTextColor] = useState("#ffffff");
  const [userBubbleBg, setUserBubbleBg] = useState("#e1f5fe");
  const [userTextColor, setUserTextColor] = useState("#000000");
  const [responseBubbleBg, setResponseBubbleBg] = useState("#fff3e0");
  const [responseTextColor, setResponseTextColor] = useState("#000000");
  const [avatar, setAvatar] = useState(null);
  const [agent, setAgent] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(defaultUrl);
  const [agentPreview, setAgentPreview] = useState(defaultAgentUrl);

  // ── Flow state ───────────────────────────────────────────────────────────────
  const [conversationFlow, setConversationFlow] = useState([]);
  const [questionForm, setQuestionForm] = useState(createEmptyQuestion());
  const [isEditingQuestion, setIsEditingQuestion] = useState(false);
  const [editQuestionId, setEditQuestionId] = useState(null);
  const [draggedQuestionIndex, setDraggedQuestionIndex] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, questionId: null });

  // ── Flow state ──────────────────────────────────────────────────────────────
  const customQuestionCount = conversationFlow.length;
  const activeQuestionCount = conversationFlow.filter((question) => question.is_active).length;

  // ── Persist flow to localStorage ─────────────────────────────────────────────
  useEffect(() => {
    if (conversationFlow.length) {
      localStorage.setItem(conversationFlowStorageKey, JSON.stringify(conversationFlow));
    } else {
      localStorage.removeItem(conversationFlowStorageKey);
    }
  }, [conversationFlow]);

  // ── Fetch / apply theme ──────────────────────────────────────────────────────
  const fetchQuestions = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/chatbot-questions");
      const questions = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
        ? response.data.data
        : [];
      const normalized = normalizeConversationFlow(questions);
      setConversationFlow(normalized);
      if (normalized.length) {
        localStorage.setItem(conversationFlowStorageKey, JSON.stringify(normalized));
      }
    } catch (error) {
      console.error("Error getting chatbot questions:", error);
      const fallbackFlow = localStorage.getItem(conversationFlowStorageKey);
      if (fallbackFlow) {
        setConversationFlow(normalizeConversationFlow(fallbackFlow));
      }
    }
  }, []);

  const applyTheme = useCallback((data = {}) => {
    setChatbotTitle(data.chatbotTitle || data.chatbot_title || "Welcome to ChatBot");
    setWelcomeMessage(data.welcomeMessage || data.welcome_message || "How can I help you today!");
    setHeaderBg(data.headerBg || data.header_bg_color || "#007bff");
    setHeaderTextColor(data.headerTextColor || data.header_text_color || "#ffffff");
    setUserBubbleBg(data.userBubbleBg || data.user_request_bg || "#e1f5fe");
    setUserTextColor(data.userTextColor || data.user_text_color || "#000000");
    setResponseBubbleBg(data.responseBubbleBg || data.response_bg || "#fff3e0");
    setResponseTextColor(data.responseTextColor || data.response_text_color || "#000000");
    const avatarUrl = data.avatarUrl || data.avatar_icon || defaultUrl;
    const agentUrl = data.agentUrl || data.agent_icon || defaultAgentUrl;
    setAvatar(avatarUrl);
    setAgent(agentUrl);
    setAvatarPreview(avatarUrl);
    setAgentPreview(agentUrl);
  }, []);

  const loadTheme = useCallback(async () => {
    const localTheme = localStorage.getItem(chatbotThemeStorageKey);
    if (localTheme) {
      try { applyTheme(JSON.parse(localTheme)); } catch (e) { console.error(e); }
    }

    try {
      const response = await axiosInstance.get("/org/theme-setting");
      const data = response.data;
      if (data) {
        if (!localTheme) applyTheme(data);
      }
    } catch (error) {
      console.error("Error getting theme:", error);
    }
  }, [applyTheme]);

  useEffect(() => { loadTheme(); }, [loadTheme]);
  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const resetAvatarUploader = () => {
    const el = document.getElementById("avatarInput");
    if (el) el.value = "";
  };
  const resetAgentUploader = () => {
    const el = document.getElementById("agentInput");
    if (el) el.value = "";
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/gif"];
    if (!allowed.includes(file.type)) {
      NotificationService.error("Please select a valid image file (PNG, JPEG, JPG, GIF).");
      return;
    }
    if (file.size > 10 * 1024) {
      NotificationService.error("File size should be less than 10KB.");
      return;
    }
    setAvatar(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleAgentIconChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/gif"];
    if (!allowed.includes(file.type)) {
      NotificationService.error("Please select a valid image file (PNG, JPEG, JPG, GIF).");
      return;
    }
    if (file.size > 10 * 1024) {
      NotificationService.error("File size should be less than 10KB.");
      return;
    }
    setAgent(file);
    setAgentPreview(URL.createObjectURL(file));
  };

  const resetTheme = () => {
    localStorage.removeItem(chatbotThemeStorageKey);
    localStorage.removeItem(conversationFlowStorageKey);
    loadTheme();
    resetAvatarUploader();
    resetAgentUploader();
    setQuestionForm(createEmptyQuestion());
    setIsEditingQuestion(false);
    setEditQuestionId(null);
    setDraggedQuestionIndex(null);
  };

  const handleQuestionFormChange = (field, value) =>
    setQuestionForm((prev) => ({ ...prev, [field]: value }));

  const resetQuestionForm = () => {
    setQuestionForm(createEmptyQuestion());
    setIsEditingQuestion(false);
    setEditQuestionId(null);
  };

  const handleEditQuestion = (question) => {
    if (!question.question_id) return;
    setIsEditingQuestion(true);
    setEditQuestionId(question.question_id);
    setQuestionForm({
      id: question.question_id,
      question_label: question.question_label || "",
      input_type: question.input_type || "text",
      field_key: question.field_key || "",
      is_active: question.is_active === true || question.is_active === 1,
      options_text: question.options_text || "",
    });
    setTimeout(() => document.getElementById("questionLabelInput")?.focus(), 0);
  };

  const openDeleteDialog = (questionId) => {
    if (!questionId) return;
    setConfirmDialog({ isOpen: true, questionId });
  };

  const closeDeleteDialog = () =>
    setConfirmDialog({ isOpen: false, questionId: null });

  const handleConfirmDelete = () => {
    handleDeleteQuestion(confirmDialog.questionId);
    closeDeleteDialog();
  };

  const handleDeleteQuestion = (questionId) => {
    axiosInstance
      .delete(`/chatbot-questions/${questionId}`)
      .then(() => {
        NotificationService.success("Question deleted successfully!");
        fetchQuestions();
        if (isEditingQuestion && editQuestionId === questionId) resetQuestionForm();
      })
      .catch((error) => {
        console.error("Error deleting chatbot question:", error);
        NotificationService.error("Failed to delete question.");
      });
  };

  const saveQuestionOrder = async (questions) => {
  try {
    const orderings = questions.map((question, index) => ({
      id: question.question_id,
      display_order: index + 1,
    }));

    await axiosInstance.put(
      "/chatbot-questions/reorder",
      { orderings }
    );

    NotificationService.success("Question order updated successfully!");
  } catch (error) {
    console.error("Error updating question order:", error);
    NotificationService.error("Failed to update question order.");
    fetchQuestions(); // revert to DB order
  }
};

  const handleToggleQuestionActive = (questionId) => {
    const question = conversationFlow.find((item) => item.id === questionId);
    if (!question) return;
    if (!question.question_id) {
      NotificationService.error("Question id is missing.");
      return;
    }
    const nextActive = !question.is_active;
    setConversationFlow((prev) =>
      prev.map((q) => (q.id === questionId ? { ...q, is_active: nextActive } : q))
    );
    axiosInstance
      .patch(`/chatbot-questions/${question.question_id}/status`, { is_active: nextActive })
      .then(() => {
        NotificationService.success(`Question ${nextActive ? "enabled" : "disabled"} successfully!`);
        fetchQuestions();
      })
      .catch((error) => {
        console.error("Error toggling question active state:", error);
        NotificationService.error("Failed to update question status.");
        setConversationFlow((prev) =>
          prev.map((q) => (q.id === questionId ? { ...q, is_active: !nextActive } : q))
        );
      });
  };

  const handleSaveQuestion = async (event) => {
    event.preventDefault();
    if (!questionForm.question_label.trim()) {
      NotificationService.error("Question label is required.");
      return;
    }
    const payload = {
      question_label: questionForm.question_label.trim(),
      input_type: questionForm.input_type,
      field_key:
        questionForm.field_key?.trim() || slugifyFieldKey(questionForm.question_label.trim()),
      is_active: !!questionForm.is_active,
    };
    try {
      if (isEditingQuestion && editQuestionId) {
        await axiosInstance.put(`/chatbot-questions/${editQuestionId}`, payload);
        NotificationService.success("Question updated successfully!");
      } else {
        await axiosInstance.post("/chatbot-questions", payload);
        NotificationService.success("Question added successfully!");
      }
      resetQuestionForm();
      fetchQuestions();
    } catch (error) {
      console.error("Error saving chatbot question:", error);
      NotificationService.error("Failed to save question.");
    }
  };

  const handleDragStart = (event, index) => {
    setDraggedQuestionIndex(index);
    event.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (event, index) => {
    event.preventDefault();
    if (draggedQuestionIndex === null || draggedQuestionIndex === index) return;
    setConversationFlow((prev) => {
      const next = [...prev];
      const dragged = next[draggedQuestionIndex];
      next.splice(draggedQuestionIndex, 1);
      next.splice(index, 0, dragged);
      return next;
    });
    setDraggedQuestionIndex(index);
  };

  const handleDragEnd = async () => {
  setDraggedQuestionIndex(null);

  await saveQuestionOrder(conversationFlow);
};

  const uploadFile = async (file, endpoint) => {
    if (!file || !(file instanceof File)) return file;
    const formData = new FormData();
    formData.append("avatar", file);
    const response = await axiosInstance.post(endpoint, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data.uploadResponse.url;
  };

  // const saveSettings = async () => {
  //   if (!chatbotTitle.trim()) {
  //     NotificationService.error("Chatbot Title is required.");
  //     document.getElementById("chatbotTitle")?.focus();
  //     return;
  //   }
  //   if (!welcomeMessage.trim()) {
  //     NotificationService.error("Welcome Message is required.");
  //     document.getElementById("welcomeMessage")?.focus();
  //     return;
  //   }
  //   if (questionForm.question_label.trim() || questionForm.options_text.trim()) {
  //     NotificationService.error("Please add or cancel the question form before saving settings.");
  //     return;
  //   }
  //   try {
  //     const avatarUrl = await uploadFile(avatar, "/org/upload-avatar");
  //     const agentUrl = await uploadFile(agent, "/org/upload-avatar");
  //     const avatarUrlFinal = avatarUrl || avatarPreview || defaultUrl;
  //     const agentUrlFinal = agentUrl || agentPreview || defaultAgentUrl;
  //     const payload = {
  //       chatbotTitle, welcomeMessage, headerBg, headerTextColor,
  //       userBubbleBg, userTextColor, responseBubbleBg, responseTextColor,
  //       avatarUrl: avatarUrlFinal, agentUrl: agentUrlFinal,
  //     };
  //     localStorage.setItem(chatbotThemeStorageKey, JSON.stringify(payload));
  //     localStorage.setItem(
  //       conversationFlowStorageKey,
  //       JSON.stringify(conversationFlow.map((q, i) => ({ ...q, display_order: i + 1 })))
  //     );
  //     setAvatar(avatarUrlFinal);
  //     setAgent(agentUrlFinal);
  //     setAvatarPreview(avatarUrlFinal);
  //     setAgentPreview(agentUrlFinal);
  //     resetAvatarUploader();
  //     resetAgentUploader();
  //     NotificationService.success("Settings saved successfully!");
  //   } catch (error) {
  //     console.error("Error saving settings:", error);
  //     NotificationService.error("Error saving settings, please try again!");
  //   }
  // };

const saveSettings = async () => {
  if (!chatbotTitle.trim()) {
    NotificationService.error("Chatbot Title is required.");
    document.getElementById("chatbotTitle")?.focus();
    return;
  }

  if (!welcomeMessage.trim()) {
    NotificationService.error("Welcome Message is required.");
    document.getElementById("welcomeMessage")?.focus();
    return;
  }

  if (
    questionForm.question_label.trim() ||
    questionForm.options_text.trim()
  ) {
    NotificationService.error(
      "Please add or cancel the question form before saving settings."
    );
    return;
  }

  try {
    const avatarUrl = await uploadFile(avatar, "/org/upload-avatar");
    const agentUrl = await uploadFile(agent, "/org/upload-avatar");

    const avatarUrlFinal = avatarUrl || avatarPreview || defaultUrl;
    const agentUrlFinal = agentUrl || agentPreview || defaultAgentUrl;

    const payload = {
      chatbotTitle,
      welcomeMessage,
      headerBg,
      headerTextColor,
      userBubbleBg,
      userTextColor,
      responseBubbleBg,
      responseTextColor,
      avatarUrl: avatarUrlFinal,
      agentUrl: agentUrlFinal,
    };

    // Save to database
    await axiosInstance.post(
      "/org/update-theme-setting",
      payload
    );

    // Save locally
    localStorage.setItem(
      chatbotThemeStorageKey,
      JSON.stringify(payload)
    );

    localStorage.setItem(
      conversationFlowStorageKey,
      JSON.stringify(
        conversationFlow.map((q, i) => ({
          ...q,
          display_order: i + 1,
        }))
      )
    );

    setAvatar(avatarUrlFinal);
    setAgent(agentUrlFinal);
    setAvatarPreview(avatarUrlFinal);
    setAgentPreview(agentUrlFinal);

    resetAvatarUploader();
    resetAgentUploader();

    NotificationService.success("Settings saved successfully!");
  } catch (error) {
    console.error("Error saving settings:", error);

    NotificationService.error(
      error?.response?.data?.error ||
      "Error saving settings, please try again!"
    );
  }
  };

  const previewMessages = buildPreviewMessages(
    welcomeMessage,
    conversationFlow
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
      {/* ── Sidebar ── */}
      <aside
        style={{
          width: 240,
          flexShrink: 0,
          backgroundColor: "#fff",
          borderRight: "1px solid #dee2e6",
          display: "flex",
          flexDirection: "column",
          padding: "0",
        }}
      >
        {/* Sidebar header */}
        <div
          style={{
            padding: "20px 20px 16px",
            borderBottom: "1px solid #dee2e6",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <svg width="20" height="20" fill="#bb0303" viewBox="0 0 16 16">
            <path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492zM5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0z" />
            <path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.901 3.434 2.541 2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52l-.094-.319zm-2.633.283c.246-.835 1.428-.835 1.674 0l.094.319a1.873 1.873 0 0 0 2.693 1.115l.291-.16c.764-.415 1.6.42 1.184 1.185l-.159.292a1.873 1.873 0 0 0 1.116 2.692l.318.094c.835.246.835 1.428 0 1.674l-.319.094a1.873 1.873 0 0 0-1.115 2.693l.16.291c.415.764-.42 1.6-1.185 1.184l-.291-.159a1.873 1.873 0 0 0-2.693 1.116l-.094.318c-.246.835-1.428.835-1.674 0l-.094-.319a1.873 1.873 0 0 0-2.692-1.115l-.292.16c-.764.415-1.6-.42-1.184-1.185l.159-.291A1.873 1.873 0 0 0 1.945 8.93l-.319-.094c-.835-.246-.835-1.428 0-1.674l.319-.094A1.873 1.873 0 0 0 3.06 4.474l-.16-.292c-.415-.764.42-1.6 1.185-1.184l.292.159a1.873 1.873 0 0 0 2.692-1.115l.094-.319z" />
          </svg>
          <span style={{ fontWeight: 700, fontSize: 15, color: "#212529" }}>Chatbot Settings</span>
        </div>

        {/* Nav items */}
        <nav style={{ padding: "12px 0", flex: 1 }}>
          {NAV_ITEMS.map((item) => {
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 20px",
                  border: "none",
                  background: isActive ? "#e7f1ff" : "transparent",
                  color: isActive ? "#bb0303" : "#495057",
                  fontWeight: isActive ? 700 : 600,
                  fontSize: 14,
                  cursor: "pointer",
                  textAlign: "left",
                  borderLeft: isActive ? "3px solid #bb0303" : "3px solid transparent",
                  transition: "all 0.15s ease",
                }}
              >
                <span style={{ opacity: isActive ? 1 : 0.6 }}>{item.icon}</span>
                {item.label}
              </button>
            );
          })}
        </nav>
      </aside>

      {/* ── Main content ── */}
      <main style={{ flex: 1, padding: "28px 24px", overflowY: "auto" }}>
        {/* ════════════════════ CONVERSATION FLOW TAB ════════════════════ */}
        {activeTab === "flow" && (
          <div className="row g-4">
            {/* Left: flow editor */}
            <div className="col-12 col-lg-9">
              <div className="p-4 border rounded bg-white d-flex flex-column">
                <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                  <div>
                    <h4 className="mb-1 heading-large">Conversation Flow Customization</h4>
                    <p className="mb-0 details-font-value">
                      Build the chatbot questions, order, and active status without any hardcoded steps.
                    </p>
                  </div>
                  <span className="badge rounded-pill bg-primary align-self-start">
                    {activeQuestionCount} Active
                  </span>
                </div>

                {/* Question list */}
                <div className="mb-4">
                  {conversationFlow.length === 0 ? (
                    <div className="border rounded p-3 bg-light">
                      <p className="mb-0 label-text text-muted">
                        No conversation flow questions configured. You can add up to 5 questions below.
                      </p>
                    </div>
                  ) : (
                    <div className="list-group">
                      {conversationFlow.map((question, index) => {
                        const isQuestionActive = question.is_active;
                        const canModifyQuestion = !!question.question_id;

                        return (
                        <div
                          key={question.id}
                          draggable={canModifyQuestion}
                          onDragStart={(e) => canModifyQuestion && handleDragStart(e, index)}
                          onDragOver={(e) => canModifyQuestion && handleDragOver(e, index)}
                          onDragEnd={handleDragEnd}
                          className={`list-group-item list-group-item-action d-flex align-items-start justify-content-between p-3 border rounded mb-2 ${
                            draggedQuestionIndex === index ? "bg-light border-primary" : ""
                          }`}
                          style={{ cursor: canModifyQuestion ? "grab" : "default", transition: "all 0.2s ease" }}
                        >
                          <div className="d-flex align-items-start gap-2 flex-grow-1" style={{ minWidth: 0 }}>
                            {canModifyQuestion && (
                              <div className="text-muted me-2" style={{ cursor: "grab", color: "#bb0303", display: "flex", alignItems: "center", paddingTop: 2 }}>
                                <svg width="18" height="18" fill="currentColor" viewBox="0 0 16 16" style={{ color: "#bb0303", transform: "scale(1.15)", filter: "drop-shadow(0 1px 2px rgba(13,110,253,0.45))" }} aria-hidden="true">
                                  <path d="M7 2a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM5 5a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm5 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM5 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm5 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm-5 3a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm5 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm-5 3a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm5 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                                </svg>
                              </div>
                            )}
                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                              <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                                <span className="badge rounded-pill bg-primary" style={{ fontSize: 10 }}>
                                  {getInputTypeLabel(question.input_type)}
                                </span>
                                <span className={`badge rounded-pill ${isQuestionActive ? "bg-success" : "bg-secondary"}`} style={{ fontSize: 10 }}>
                                  {isQuestionActive ? "Active" : "Disabled"}
                                </span>
                              </div>
                              <div className="label-text text-truncate" title={question.question_label} style={{ fontWeight: 600 }}>
                                {question.question_label}
                              </div>
                              {["dropdown", "multiple_choice"].includes(question.input_type) && (
                                <div className="details-font-value mt-1">
                                  Options: {parseFlowOptions(question.options_text).slice(0, 3).join(", ")}
                                  {parseFlowOptions(question.options_text).length > 3 ? "..." : ""}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="d-flex align-items-center gap-2 ms-3 flex-shrink-0">
                            <div
                              className="form-check form-switch mb-0"
                              title="Toggle active status"
                            >
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={!!isQuestionActive}
                                onChange={() => handleToggleQuestionActive(question.id)}
                                style={{ cursor: "pointer", width: 32, height: 16 }}
                              />
                            </div>
                            {canModifyQuestion && (
                              <>
                                <button type="button" className="btn btn-sm btn-outline-secondary border-0 p-1" onClick={() => handleEditQuestion(question)} title="Edit question">
                                  <svg width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                                    <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger border-0 p-1"
                                  onClick={() => openDeleteDialog(question.question_id)}
                                  title="Delete question"
                                >
                                  <svg width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
                                    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3h11V2h-11v1z" />
                                  </svg>
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                      })}
                    </div>
                  )}
                </div>

                {/* Add/Edit form */}
                {customQuestionCount === 5 ? (
                  <div></div>
                ):
                <form onSubmit={handleSaveQuestion} className="border-top border-3 border-dark pt-3">
                  <label className="form-label label-text fw-bold mb-3">
                    {isEditingQuestion ? "Edit Question" : "Add Question"}
                  </label>
                  <div className="mb-3">
                    <label className="form-label label-text">
                      Question Label <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control label-text"
                      id="questionLabelInput"
                      value={questionForm.question_label}
                      onChange={(e) => handleQuestionFormChange("question_label", e.target.value)}
                      placeholder="e.g. What is your company name?"
                      maxLength={180}
                      required
                    />
                  </div>
                  <div className="d-flex flex-wrap gap-3 mb-2 justify-content-between">
                    <div className="form-check form-switch mb-0">


                    </div>
                    <div>
                      {isEditingQuestion && (
                      <button
                        type="button"
                        className="btn btn-secondary label-text"
                        onClick={resetQuestionForm}
                      >
                        Cancel
                      </button>
                    )}

                    <button
                      type="submit"
                      className="btn btn-color label-text"
                    >
                      {isEditingQuestion ? "Update Question" : "Add Question"}
                    </button>
                    </div>
                    
                  </div>
                  
                </form>
                }

              </div>
            </div>

          </div>
        )}

        {/* ════════════════════ APPEARANCE TAB ════════════════════ */}
        {activeTab === "appearance" && (
          <div className="row">
            <div className="col-12 col-lg-8 col-xl-6 flex-grow-1 ">
              <div className="p-4 border rounded bg-white d-flex flex-column">
                <h4 className="mb-4 heading-large">Customize Your Chatbot Appearance</h4>

                <div className="mb-3">
                  <label className="form-label label-text fw-bold">
                    Chatbot Title <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control label-text"
                    id="chatbotTitle"
                    maxLength={45}
                    value={chatbotTitle}
                    onChange={(e) => setChatbotTitle(e.target.value)}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label label-text fw-bold">
                    Welcome Message <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control label-text"
                    id="welcomeMessage"
                    maxLength={150}
                    value={welcomeMessage}
                    onChange={(e) => {
                      const lines = e.target.value.split("\n");
                      if (lines.length <= 3) setWelcomeMessage(e.target.value);
                    }}
                    style={{ resize: "none", overflowY: "auto", height: "4.5em" }}
                    required
                  />
                </div>

                <ColorPicker label="Header Background Color" value={headerBg} onChange={setHeaderBg}/>
                <ColorPickerText label="Header Text Color" value={headerTextColor} onChange={setHeaderTextColor} />
                <ColorPicker label="User Request Bubble Background" value={userBubbleBg} onChange={setUserBubbleBg} />
                <ColorPickerText label="User Request Text Color" value={userTextColor} onChange={setUserTextColor} />
                <ColorPicker label="Response Background" value={responseBubbleBg} onChange={setResponseBubbleBg} />
                <ColorPickerText label="Response Text Color" value={responseTextColor} onChange={setResponseTextColor} />

                <div className="mb-3">
                  <label className="form-label label-text fw-bold">Avatar Icon</label>
                  <input type="file" className="form-control label-text" accept="image/*" onChange={handleAvatarChange} id="avatarInput" />
                  <p className="details-font-value mt-3 mb-0">
                    <ul>
                      <li>Supported formats: .png, .gif, .jpeg, and .jpg</li>
                      <li>Maximum file size: 10KB</li>
                      <li>Square image with transparent background is recommended.</li>
                    </ul>
                  </p>
                </div>

                <div className="mb-3">
                  <label className="form-label label-text fw-bold">Agent Icon</label>
                  <input type="file" className="form-control label-text" accept="image/*" onChange={handleAgentIconChange} id="agentInput" />
                  <p className="details-font-value mt-3 mb-0">
                    <ul>
                      <li>Supported formats: .png, .gif, .jpeg, and .jpg</li>
                      <li>Maximum file size: 10KB</li>
                      <li>Square image with transparent background is recommended.</li>
                    </ul>
                  </p>
                </div>

                <div className="mt-3 d-flex justify-content-end">
                  <button type="button" className="btn btn-danger btn-sm me-2" onClick={resetTheme} style={{ fontWeight: 700 }}>
                    Reset
                  </button>
                  <button className="btn btn-color" onClick={saveSettings}>
                    Save Settings
                  </button>
                </div>
              </div>
            </div>
                        <div className="col-12 col-lg-5 d-flex flex-column gap-3">
              {/* Chatbot Preview */}
              <div className="p-4 border rounded bg-white">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="mb-0 heading-large">Chatbot Preview</h4>
                  <button
                    className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                    onClick={() => setConversationFlow((prev) => [...prev])}
                    title="Refresh preview"
                    type="button"
                  >
                    <svg width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                      <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z" />
                      <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z" />
                    </svg>
                    <span>Replay</span>
                  </button>
                </div>
                <div className="border rounded-2">
                  <div className="p-2 rounded d-flex align-items-center" style={{ backgroundColor: headerBg, color: headerTextColor }}>
                    <img src={avatarPreview || defaultUrl} alt="Avatar" className="rounded-circle me-2" style={{ width: 40, height: 40 }} />
                    <b><span className="heading-small">{chatbotTitle}</span></b>
                  </div>
                  <div className="mt-3 flex-grow-1 overflow-auto" style={{ minHeight: 250, maxHeight: 500 }}>
                    {previewMessages.length === 0 ? (
                      <div className="p-3">
                        <div className="d-flex align-items-start">
                          <img src={avatarPreview || defaultUrl} alt="Avatar" className="rounded-circle me-2" style={{ width: 40, height: 40 }} />
                          <div className="p-2 rounded label-text" style={{ backgroundColor: responseBubbleBg, color: responseTextColor, maxWidth: "70%" }}>
                            Add a welcome message or active questions to see the preview here.
                          </div>
                        </div>
                      </div>
                    ) : (
                      previewMessages.map((msg, index) => (
                        <div key={`${msg.type}-${index}`} className={`d-flex p-2 mb-2 ${msg.type === "user" ? "justify-content-end" : "justify-content-start"}`}>
                          {msg.type === "bot" && (
                            <img src={avatarPreview || defaultUrl} alt="Avatar" className="rounded-circle me-2" style={{ width: 40, height: 40, alignSelf: "flex-start" }} />
                          )}
                          <div className="p-2 rounded label-text" style={{ backgroundColor: msg.type === "user" ? userBubbleBg : responseBubbleBg, color: msg.type === "user" ? userTextColor : responseTextColor, maxWidth: "70%", display: "flex", alignItems: "center", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                            <div>
                              <div style={{ fontWeight: 600 }}>{msg.text}</div>
                              {msg.meta && msg.type === "bot" && (
                                <div className="details-font-value mt-1">{getInputTypeLabel(msg.meta.input_type)}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Chatbot Icon Preview */}
              <div className="p-4 border rounded bg-white">
                <h4 className="mb-3 heading-large">Chatbot Icon Preview</h4>
                <div className="d-flex justify-content-end">
                  <div className="chat-bot-chat-icon" style={{ backgroundColor: headerBg }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20 2H4C2.897 2 2 2.897 2 4v13c0 1.103.897 2 2 2h14l4 4V4c0-1.103-.897-2-2-2zM4 4h16v12.586L17.414 14H4V4z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Agent Icon Preview */}
              <div className="p-4 border rounded bg-white">
                <h4 className="mb-3 heading-large">Agent Icon Preview</h4>
                <div className="d-flex justify-content-end">
                  <img src={agentPreview || defaultAgentUrl} alt="Agent" className="rounded-circle" style={{ width: 60, height: 60 }} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════ SUBSCRIPTION TAB ════════════════════ */}
        {activeTab === "subscription" && (
          <div className="row">
            <div className="col-12 col-lg-10">
              <div className="p-4 border rounded bg-white">
                <h4 className="mb-4 heading-large">Subscription</h4>
                <DisplaySubsComponent />
              </div>
            </div>
          </div>
        )}
      </main>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title="Delete Question"
        message={
    <>
      Are you sure you want to delete this question? This will also{" "}
      <strong>delete all answers</strong> related to it.
    </>
  }
        onConfirm={handleConfirmDelete}
        onCancel={closeDeleteDialog}
      />
    </div>
  );
};

export default SetupPage;
